import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import Icon2D from '../../assets/toolbar_icons/Icon2D.svg?react';
import Icon3D from '../../assets/toolbar_icons/Icon3D.svg?react';
import IconSettings from '../../assets/toolbar_icons/IconSettings.svg?react';
import RedoToolbar from '../../assets/toolbar_icons/RedoToolbar.svg?react';
import UmbrellaToolbar from '../../assets/toolbar_icons/UmbrellaToolbar.svg?react';
import UndoToolbar from '../../assets/toolbar_icons/UndoToolbar.svg?react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useStore } from '../../pages/workspace/hooks';
import { setUseOriginalTextures } from '../../store/reducers/workspace.reducer';
import {
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isCalculationCompleted as checkIsCalculationCompleted,
  isCalculationStarted as checkIsCalculationStarted,
} from '../../utils/jobStatus';
import { isMarketMatchedVersion } from '../../utils/lampMarketMatch';

/**
 * Small grid icon used by the texture toggle. Rendered inline (rather than
 * imported as an .svg) because the "off" state needs an overlaid diagonal
 * slash that we can't conditionally hide inside a static SVG asset.
 *
 * `currentColor` so the button controls the color: white when off, brand
 * green when on — same pattern as the other bottom-toolbar toggles.
 */
function TextureGridIcon({ on }: { on: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="14" height="14" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
      <line x1="6.67" y1="2.4" x2="6.67" y2="15.6" stroke="currentColor" strokeWidth="1.1" />
      <line x1="11.33" y1="2.4" x2="11.33" y2="15.6" stroke="currentColor" strokeWidth="1.1" />
      <line x1="2.4" y1="6.67" x2="15.6" y2="6.67" stroke="currentColor" strokeWidth="1.1" />
      <line x1="2.4" y1="11.33" x2="15.6" y2="11.33" stroke="currentColor" strokeWidth="1.1" />
      {!on && (
        // Slash runs from top-right outside the grid down to bottom-left
        // outside the grid (a couple of px overshoot on both ends) so it
        // clearly reads as a "crossed-out" overlay rather than a gridline.
        <line x1="17.5" y1="0.5" x2="0.5" y2="17.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      )}
    </svg>
  );
}

interface BottomToolbarProps {
  showExportsPanel: boolean;
  /** When false, hides the 2D/3D toggle (real-time render is always perspective). */
  show2D3DToggle?: boolean;
}

export function BottomToolbar({ showExportsPanel, show2D3DToggle = true }: BottomToolbarProps) {
  const { t } = useTranslation();
  const [showSettings, setShowSettings] = useState(false);
  const dispatch = useAppDispatch();
  const useOriginalTextures = useAppSelector((state) => state.workspace.useOriginalTextures);

  const {
    lamps,
    addLamp,
    removeLamp,
    isOrthographicView,
    setOrthographicView,
    setTargetCameraPosition,
    activeVersion,
    lampHistory,
    undoLampAction,
    redoLampAction,
    showCompass,
    setShowCompass,
    showGrid,
    setShowGrid,
    showIESModels,
    setShowIESModels,
    showMeasurementPoints,
    setShowMeasurementPoints,
    showNearestLampDistancesWhileMoving,
    setShowNearestLampDistancesWhileMoving,
    showReferenceImage,
    setShowReferenceImage,
    project,
    activeViewModes,
  } = useStore();

  const hasReferenceImage = !!project?.referenceImageUrl && !!activeViewModes?.object;

  const isCalculationStarted = checkIsCalculationStarted(activeVersion);
  const isCalculationCompleted = checkIsCalculationCompleted(activeVersion);
  const isAutopositioningInProgress = checkIsAutopositioningInProgress(activeVersion);
  // Market-matched versions have photometry (catalogue variant LDCs) and frontend
  // measurements without an ML calculation job.
  const hasPhotometricData = isCalculationCompleted || isMarketMatchedVersion(activeVersion);
  const isCalculationNotStarted = !isCalculationStarted;
  const isUndoRedoDisabled = isAutopositioningInProgress || !isCalculationNotStarted;

  const canUndo = !isUndoRedoDisabled && lampHistory.undoStack.length > 0;
  const canRedo = !isUndoRedoDisabled && lampHistory.redoStack.length > 0;

  const selectedLamp = lamps.find((lamp) => lamp.isSelected);
  const canModify = isCalculationNotStarted && !isAutopositioningInProgress;

  // ── Keyboard shortcuts (Undo / Redo) ──────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Undo: Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          undoLampAction();
        }
      }

      // Redo: Cmd+Shift+Z or Cmd+Y (Mac) or Ctrl+Shift+Z or Ctrl+Y (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (canRedo) {
          redoLampAction();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undoLampAction, redoLampAction]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDuplicate = () => {
    if (!selectedLamp || !canModify) return;
    const { id: _id, uuid: _uuid, isSelected: _isSelected, lumenOutput: _lumenOutput, ...lampWithoutId } = selectedLamp;
    addLamp({
      ...lampWithoutId,
      uuid: crypto.randomUUID(),
      name: `${lampWithoutId.name} (copy)`,
    });
  };

  const handleDelete = () => {
    if (!selectedLamp || !canModify) return;
    removeLamp(selectedLamp.id);
  };

  const handleViewFocus = () => {
    if (!selectedLamp) return;
    const [x, y, z] = selectedLamp.position;
    setTargetCameraPosition({
      position: [x, y, z + 20],
      lookAt: [x, y, z],
    });
  };

  const handleToggleProjection = () => {
    setOrthographicView(!isOrthographicView);
  };

  if (showExportsPanel) {
    return null;
  }

  const isDeleteEnabled = !!selectedLamp && canModify;
  const isDuplicateEnabled = !!selectedLamp && canModify;
  const isFocusEnabled = !!selectedLamp;

  return (
    <div
      className="bottom-[22px] left-1/2 -translate-x-1/2 flex items-center bg-[#2A2A2A] border border-white/10 rounded-xl p-1.5 shadow-2xl z-50 gap-0.5 max-w-[calc(100vw-32px)] sm:max-w-none overflow-x-auto sm:overflow-x-visible scrollbar-hide"
      style={{
        position: 'absolute',
      }}
    >
      {/* ── Section 1: History (Undo / Redo) ─────────────────────────────── */}
      <Tooltip title={`${t('workspace.toolbar.undoTool')} (⌘Z)`} placement="top">
        <button
          onClick={() => canUndo && undoLampAction()}
          disabled={!canUndo}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            canUndo ? 'text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'
          }`}
        >
          <UndoToolbar className="w-5 h-5" />
        </button>
      </Tooltip>

      <Tooltip title={`${t('workspace.toolbar.redoTool')} (⌘⇧Z)`} placement="top">
        <button
          onClick={() => canRedo && redoLampAction()}
          disabled={!canRedo}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            canRedo ? 'text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'
          }`}
        >
          <RedoToolbar className="w-5 h-5" />
        </button>
      </Tooltip>

      {/* ── Divider ──────────────────────────────────────────────────────── */}
      <div className="w-[1px] h-7 bg-white/10 mx-1.5" />

      {/* ── Section 2: Actions (Duplicate / Trash / View Focus) ──────────── */}
      <Tooltip title={t('workspace.duplicateLuminaire')} placement="top">
        <button
          onClick={handleDuplicate}
          disabled={!isDuplicateEnabled}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            isDuplicateEnabled ? 'text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'
          }`}
        >
          {/* Overlapping squares */}
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        </button>
      </Tooltip>

      <Tooltip title={t('workspace.deleteLuminaire')} placement="top">
        <button
          onClick={handleDelete}
          disabled={!isDeleteEnabled}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            isDeleteEnabled ? 'text-white hover:bg-white/10 hover:text-red-400' : 'text-gray-600 cursor-not-allowed'
          }`}
        >
          {/* Trash icon */}
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      </Tooltip>

      <Tooltip title={t('workspace.findLuminaire')} placement="top">
        <button
          onClick={handleViewFocus}
          disabled={!isFocusEnabled}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            isFocusEnabled ? 'text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'
          }`}
        >
          {/* Eye icon */}
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      </Tooltip>

      {/* ── Divider ──────────────────────────────────────────────────────── */}
      <div className="w-[1px] h-7 bg-white/10 mx-1.5" />

      {/* ── Section 3: View Controls (2D / 3D Toggle) — hidden in real-time render ── */}
      {show2D3DToggle && (
        <>
          <Tooltip
            title={isOrthographicView ? t('workspace.toolbar.switchTo3D') : t('workspace.toolbar.switchTo2D')}
            placement="top"
          >
            <button
              onClick={handleToggleProjection}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors"
            >
              {isOrthographicView ? <Icon3D /> : <Icon2D />}
            </button>
          </Tooltip>

          {/* Texture toggle — sits between 2D/3D and the IES toggle. Brand
              green when textures are on, white grid with a diagonal slash
              when off (visual "no textures" cue). Replaces the old
              labelled pill switch that lived in the sidebar's view-mode
              row. */}
          <Tooltip title={t('common.texture')} placement="top">
            <button
              onClick={() => dispatch(setUseOriginalTextures(!useOriginalTextures))}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
              style={{ color: useOriginalTextures ? '#B3EC49' : '#FFFFFF' }}
            >
              <TextureGridIcon on={useOriginalTextures} />
            </button>
          </Tooltip>

          {/* IES models toggle — only meaningful after calculation has
              produced the photometric data. White when off, brand green when
              on; replaces the toggle row that used to live in the settings
              popover. */}
          {hasPhotometricData && (
            <Tooltip title={t('workspace.showIESModels')} placement="top">
              <button
                onClick={() => setShowIESModels(!showIESModels)}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                style={{ color: showIESModels ? '#B3EC49' : '#FFFFFF' }}
              >
                <UmbrellaToolbar />
              </button>
            </Tooltip>
          )}

          <div className="w-[1px] h-7 bg-white/10 mx-1.5" />
        </>
      )}

      {/* ── Section 4: Settings ─────────────────────────────────────────── */}
      <div className="relative">
        <Tooltip title={t('workspace.settings')} placement="top">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              showSettings ? 'text-lime-400 bg-white/10' : 'text-white hover:bg-white/10'
            }`}
          >
            <IconSettings />
          </button>
        </Tooltip>

        {showSettings && (
          <>
            <div className="fixed inset-0" style={{ zIndex: 100 }} onClick={() => setShowSettings(false)} />
            <div
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 bg-[#2A2A2A] border border-white/10 rounded-xl shadow-2xl min-w-[280px] p-3 space-y-2.5"
              style={{ zIndex: 100 }}
            >
              {/* Compass toggle */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-300 flex-1 min-w-0 leading-tight">{t('workspace.showCompass')}</span>
                <button
                  onClick={() => setShowCompass(!showCompass)}
                  className="relative w-9 h-5 shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ backgroundColor: '#000' }}
                >
                  <div
                    className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                      showCompass ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Reference image toggle (CAD polygon flow) */}
              {hasReferenceImage && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-300 flex-1 min-w-0 leading-tight">
                    {t('workspace.showReferenceImage')}
                  </span>
                  <button
                    onClick={() => setShowReferenceImage(!showReferenceImage)}
                    className="relative w-9 h-5 shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                    style={{ backgroundColor: '#000' }}
                  >
                    <div
                      className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                        showReferenceImage ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Grid toggle */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-300 flex-1 min-w-0 leading-tight">{t('workspace.showGrid')}</span>
                <button
                  onClick={() => setShowGrid(!showGrid)}
                  className="relative w-9 h-5 shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ backgroundColor: '#000' }}
                >
                  <div
                    className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                      showGrid ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Measurement Points toggle (only with measurement results) */}
              {hasPhotometricData && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-300 flex-1 min-w-0 leading-tight">
                    {t('workspace.showMeasurementPoints')}
                  </span>
                  <button
                    onClick={() => setShowMeasurementPoints(!showMeasurementPoints)}
                    className="relative w-9 h-5 shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                    style={{ backgroundColor: '#000' }}
                  >
                    <div
                      className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                        showMeasurementPoints ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Drag lamp distance preview toggle */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-300 flex-1 min-w-0 leading-tight">
                  {t('workspace.showNearestLampDistancesWhileMoving')}
                </span>
                <button
                  onClick={() => setShowNearestLampDistancesWhileMoving(!showNearestLampDistancesWhileMoving)}
                  className="relative w-9 h-5 shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ backgroundColor: '#000' }}
                >
                  <div
                    className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                      showNearestLampDistancesWhileMoving ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
